import { TFunction } from 'i18next'
import { DedicatedServerHardware, DedicatedServerOperation } from '../../../api/bareMetalTypes'
import { handleOsIcons } from '../../../hooks/useOsIcons'

export const stateColors = {
  ok: '#229B61',
  warn: '#E8B533',
  err: '#E85048',
  mute: '#858A94',
  blue: '#2894FF',
}

const unitPower: Record<string, number> = { b: 0, k: 1, kb: 1, m: 2, mb: 2, g: 3, gb: 3, t: 4, tb: 4, p: 5, pb: 5 }
const unitNames = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']

const trim = (value: number) => String(Number(value.toFixed(value >= 100 ? 0 : 2)))

// The provider sends short units ('m', 'g'). Memory is binary. Disks are decimal,
// except sizes that are exact multiples of 1024 (1024 g is a 1 TB disk, not 1.02 TB).
export const formatCapacity = (value?: number | null, unit?: string | null, binary = false) => {
  if (value === null || value === undefined || !Number.isFinite(value) || value < 0) return '—'
  const power = unitPower[(unit || '').trim().toLowerCase()]
  if (power === undefined) return `${value}${unit ? ` ${unit}` : ''}`
  const base = binary || (value >= 1024 && value % 1024 === 0) ? 1024 : 1000
  let index = power
  let converted = value
  while (converted >= base && index < unitNames.length - 1) {
    converted /= base
    index += 1
  }
  return `${trim(converted)} ${unitNames[index]}`
}

export const diskTypeLabel = (type?: string | null) => {
  switch ((type || '').toUpperCase()) {
    case 'NVME': return 'NVMe'
    case 'SSD': return 'SSD'
    case 'HDD': return 'HDD'
    default: return ''
  }
}

export const formatDisks = (disk?: DedicatedServerHardware['disk']) => {
  if (!disk) return '—'
  const types = Array.from(new Set((disk.details || []).map(item => diskTypeLabel(item.disk_type)).filter(Boolean)))
  const type = types.length ? types.join(' + ') : diskTypeLabel(disk.disk_type)
  return `${formatCapacity(disk.value, disk.value_type)}${type ? ` ${type}` : ''}`
}

export const hardwareLines = (hardware?: DedicatedServerHardware | null) => {
  if (!hardware) return { cpu: '—', memDisk: '' }
  return {
    cpu: `${hardware.cpu.count} × ${hardware.cpu.model.replace(/^Intel\s+/i, '')}`,
    memDisk: `${formatCapacity(hardware.ram.value, hardware.ram.value_type, true)} RAM · ${formatDisks(hardware.disk)}`,
  }
}

type ServerLike = { status?: string | null; power_status?: string | null; reinstall_running?: boolean }

// One status per server: power state for running servers, lifecycle state otherwise.
export const serverState = (server: ServerLike, t: TFunction) => {
  const power = server.power_status || 'unknown'
  const powerLabel = t(`DEDICATED_POWER_${power.toUpperCase()}`, { defaultValue: power })
  if (server.status === 'active' && !server.reinstall_running) {
    const color = power === 'online' ? stateColors.ok : power === 'rebooting' ? stateColors.warn : power === 'unknown' ? stateColors.mute : stateColors.err
    return { label: powerLabel, color, sub: String(t('DEDICATED_STATUS_ACTIVE')), failed: false }
  }
  const lifecycle = server.reinstall_running ? 'installing' : server.status || 'unknown'
  const color = lifecycle === 'deleting' ? stateColors.err : lifecycle === 'available' ? stateColors.mute : stateColors.warn
  return {
    label: String(t(`DEDICATED_STATUS_${lifecycle.toUpperCase()}`, { defaultValue: lifecycle })),
    color,
    sub: `${t('DEDICATED_POWER_STATUS')}: ${String(powerLabel).toLocaleLowerCase()}`,
    failed: false,
  }
}

export const osIcon = (os?: string | null) => {
  const name = (os || '').toLowerCase()
  const key = ['almalinux', 'rockylinux', 'centos', 'debian', 'ubuntu', 'fedora', 'freebsd', 'opensuse', 'windows']
    .find(item => name.replace(/\s+/g, '').includes(item))
    || (name.includes('rocky') ? 'rockylinux' : name.includes('alma') ? 'almalinux' : name.includes('red hat') || name.includes('rhel') ? 'rhel' : '')
  return key ? handleOsIcons(key) : null
}

type ProgressStep = { name?: string; display?: string; status?: string }
type ProgressObject = { completed_steps?: number; total_steps?: number; steps_details?: ProgressStep[] }

// Progress is normally {completed_steps, total_steps, steps_details}; numbers and text are tolerated.
export const operationProgress = (operation: { progress?: unknown }) => {
  const progress = operation.progress
  if (typeof progress === 'number' && Number.isFinite(progress)) {
    return { pct: Math.max(0, Math.min(100, Math.round(progress))), step: '' }
  }
  if (typeof progress === 'string') {
    const text = progress.trim()
    const match = text.match(/(\d{1,3})\s*%/)
    return { pct: match ? Math.min(100, Number(match[1])) : null, step: text.replace(/[—–-]?\s*\d{1,3}\s*%/, '').trim() }
  }
  if (progress && typeof progress === 'object') {
    const { completed_steps: done, total_steps: total, steps_details: steps = [] } = progress as ProgressObject
    const current = steps.find(item => item.status === 'in progress') || steps.find(item => item.status !== 'succeeded')
    const pct = typeof done === 'number' && typeof total === 'number' && total > 0
      ? Math.max(0, Math.min(100, Math.round(done / total * 100))) : null
    return { pct, step: current?.display || current?.name || '' }
  }
  return { pct: null, step: '' }
}

const groupLabels = (items: { size: string; type: string }[]) => {
  const groups = new Map<string, number>()
  items.forEach(item => {
    const key = `${item.size} ${item.type}`.trim()
    groups.set(key, (groups.get(key) || 0) + 1)
  })
  return Array.from(groups, ([label, count]) => `${count} × ${label}`)
}

export const diskGroups = (disk?: DedicatedServerHardware['disk']) => {
  if (!disk) return []
  if (disk.details?.length) {
    return groupLabels(disk.details.map(item => ({ size: formatCapacity(item.value, item.value_type), type: diskTypeLabel(item.disk_type) })))
  }
  if (!disk.count) return []
  return [`${disk.count} × ${formatCapacity(disk.value / disk.count, disk.value_type)} ${diskTypeLabel(disk.disk_type)}`.trim()]
}

export const memoryLabel = (ram?: DedicatedServerHardware['ram']) => {
  if (!ram) return '—'
  const total = formatCapacity(ram.value, ram.value_type, true)
  return ram.count > 1 ? `${total} (${ram.count} × ${formatCapacity(ram.value / ram.count, ram.value_type, true)})` : total
}

export const preorderHardware = (hardware?: DedicatedServerHardware | null) => ({
  cpu: hardware ? `${hardware.cpu.count} × ${hardware.cpu.model}` : '—',
  ram: memoryLabel(hardware?.ram),
  disk: diskGroups(hardware?.disk).join(', ') || '—',
})

export const formatShortDate = (value: string | null | undefined, language?: string) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString(language?.startsWith('uk') ? 'uk-UA' : 'en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}
